import { FC, ChangeEvent, useEffect, useRef, useState } from 'react';
import './StepText.scss';
import { ReactComponent as IconEdit } from 'assets/img/icons/icon_Edit.svg';
import { ReactComponent as IconEye } from 'assets/img/icons/icon_Eye.svg';

interface Props {
  watermarkText: string;
  onChange: (text: string) => void;
  onApply: () => void;
  onBack: () => void;
  isProcessing: boolean;
}

const DEFAULT_TEXT = 'Document protégé par ID Protect';
const TYPING_SPEED = 45;      // ms par caractère
const PAUSE_FULL = 800;       // pause une fois le texte complet
const PAUSE_CLEARED = 350;    // pause après effacement, avant la 2e frappe
const REPEAT_COUNT = 2;

// L'animation ne joue que la toute première fois où l'étape est affichée
// (survit aux allers-retours entre étapes, remis à zéro au rechargement de page).
let hasPlayedTyping = false;

const StepText: FC<Props> = ({ watermarkText, onChange, onApply, onBack, isProcessing }) => {
  const [isTyping, setIsTyping] = useState(
    () => !hasPlayedTyping && watermarkText === DEFAULT_TEXT
  );
  const [typedText, setTypedText] = useState('');
  const cancelledRef = useRef(false);

  useEffect(() => {
    if (!isTyping) return;
    hasPlayedTyping = true;

    let charIndex = 0;
    let cycle = 1;
    let timer: ReturnType<typeof setTimeout>;

    const tick = () => {
      if (cancelledRef.current) return;
      if (charIndex <= DEFAULT_TEXT.length) {
        setTypedText(DEFAULT_TEXT.slice(0, charIndex));
        charIndex += 1;
        timer = setTimeout(tick, TYPING_SPEED);
      } else if (cycle < REPEAT_COUNT) {
        cycle += 1;
        charIndex = 0;
        timer = setTimeout(() => {
          if (cancelledRef.current) return;
          setTypedText('');
          timer = setTimeout(tick, PAUSE_CLEARED);
        }, PAUSE_FULL);
      } else {
        setIsTyping(false);
      }
    };

    timer = setTimeout(tick, 300);
    return () => clearTimeout(timer);
  }, [isTyping]);

  // Un clic / focus pendant l'animation l'interrompt et restaure le texte complet
  const skipTyping = () => {
    if (!isTyping) return;
    cancelledRef.current = true;
    setIsTyping(false);
  };

  const displayValue = isTyping ? typedText : watermarkText;

  return (
    <div className="step-text">
      <div className="step-text__area">
        <h3 className="step-text__title">Texte personnalisable</h3>
        <p className="step-text__subtitle">
          Vous pouvez modifier le texte qui apparaîtra sur le document
        </p>

        <div className="step-text__input-wrap">
          <div className={`step-text__inner${isTyping ? ' step-text__inner--typing' : ''}`}>
            {isTyping ? (
              <span
                className="step-text__typing-text"
                onClick={skipTyping}
                role="textbox"
                aria-label="Texte du filigrane"
              >
                {typedText}
                <span className="step-text__caret" aria-hidden="true" />
              </span>
            ) : (
              <input
                className="step-text__input"
                type="text"
                value={watermarkText}
                onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
                placeholder="Texte du filigrane"
                maxLength={80}
                aria-label="Texte du filigrane"
              />
            )}
            <div className="step-text__edit-icon p-2">
              <IconEdit fill={"#e5e5e5"} width={20}/>
            </div>
          </div>
        </div>
        <span className="step-text__counter">{displayValue.length} / 80</span>

        <div className="step-text__actions">
          <button className="step-text__back-btn" onClick={onBack} type="button">
            ← Retour
          </button>
          <button
            className="step-text__apply-btn"
            onClick={onApply}
            type="button"
            disabled={isProcessing || watermarkText.trim().length === 0}
          >
            <IconEye fill={"#fff"} width={20}/>
            <span>{isProcessing ? 'Traitement…' : 'Aposer le filigrane'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default StepText;
